import { CampaignPausedBanner } from '../../components/CampaignPausedBanner';
import { ReviewReleaseBanner } from './ReviewReleaseBanner';
import { CampaignLimitsBanner } from './CampaignLimitsBanner';
import React, { useEffect, useState } from 'react';
import {
  ClaimStatePill,
  queueTaskState,
  queueTaskStateLabel,
  totalAvailable,
  totalClaimable,
  type QueueTaskState,
} from './queueParts';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  ClipboardCheck,
  Eye,
  Loader2,
  PlayCircle,
  RotateCcw,
  X,
} from 'lucide-react';
import { CampaignProgressBar } from '../../components/CampaignProgressBar';
import type { CampaignProgressBarVariant } from '../../components/CampaignProgressBar';
import { Logo } from '../../components/Logo';
import { UserMenu } from '../../components/UserMenu';
import { SearchableDropdown } from '../../components/SearchableDropdown';
import { Task } from '../../types';
import type { CampaignStatusCounts } from '../../services/tasks';
import { ClaimOption, TaskQueueViewProps, TabId } from './types';
import { paths } from '../../routes/paths';
import { useIsPhone } from '../../hooks/useIsMobile';

export function MobileTaskQueueView({
  vm,
  tabs,
  effectiveTab,
  isReviewer,
  canReviewSelected,
  claiming,
  claimNotice,
  taskClaimOptions,
  reviewClaimOptions,
  unassignedReviewCount,
  onTabChange,
  onClaimNext,
  onClaimNextReview,
  onBrowseUnassigned,
}: TaskQueueViewProps) {
  const isPhone = useIsPhone();
  const [claimSheet, setClaimSheet] = useState<'task' | 'review' | null>(null);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <ClipboardCheck className="h-7 w-7 text-primary shrink-0" />
            <Logo className="text-xl font-bold text-gray-900 truncate" />
          </div>
          <UserMenu />
        </div>
      </header>

      <main className="px-4 py-4 pb-8">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Queue</h1>
          <p className="mt-1 text-sm text-gray-500">
            Claim work, track active tasks, and pick up reviews.
          </p>
        </div>

        <MobileCampaignSelect
          campaigns={vm.campaigns}
          selectedId={vm.selectedCampaignId}
          onChange={vm.setSelectedCampaignId}
        />

        {vm.selectedCampaign?.paused ? <CampaignPausedBanner /> : <MobileQueueState vm={vm} />}

        {!vm.selectedCampaign?.paused && !vm.busy && !vm.error && (
          <>
            <CampaignLimitsBanner message={vm.campaignLimitsMessage} />
            <MobileTabs tabs={tabs} activeId={effectiveTab} onChange={onTabChange} />
            {effectiveTab === 'my' && (
              <div className="space-y-5">
                <MobileTaskSection
                  icon={<PlayCircle className="h-5 w-5 text-primary" />}
                  title="My Tasks"
                  tasks={vm.buckets.myActive}
                  empty="No tasks claimed yet."
                  statusCounts={vm.campaignStatusCounts}
                  statusVariant="simple"
                  action={
                    vm.canClaimInSelected ? (
                      <HeaderClaimButton
                        disabled={claiming || totalClaimable(taskClaimOptions) === 0}
                        onClick={() => setClaimSheet('task')}
                      />
                    ) : null
                  }
                />
              </div>
            )}
            {effectiveTab === 'reviews' && isReviewer && (
              <MobileTaskSection
                icon={<Eye className="h-5 w-5 text-primary" />}
                title="My Reviews"
                tasks={vm.buckets.myReviews}
                empty="No reviews in progress."
                showEscalation
                statusCounts={canReviewSelected ? vm.campaignStatusCounts : undefined}
                notice={canReviewSelected ? <ReviewReleaseBanner campaign={vm.selectedCampaign} /> : null}
                action={
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {canReviewSelected && !isPhone && (
                      <button
                        type="button"
                        onClick={onBrowseUnassigned}
                        disabled={claiming || unassignedReviewCount === 0}
                        className="inline-flex min-h-[40px] items-center justify-center rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-700 hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Browse
                      </button>
                    )}
                    {canReviewSelected && (
                      <HeaderClaimButton
                        disabled={claiming || totalAvailable(reviewClaimOptions) === 0}
                        onClick={() => setClaimSheet('review')}
                      />
                    )}
                  </div>
                }
              />
            )}
            {effectiveTab === 'rework' && (
              <MobileTaskSection
                icon={<RotateCcw className="h-5 w-5 text-primary" />}
                title="Rework"
                tasks={vm.buckets.changesRequested}
                empty="No revisions waiting on you."
              />
            )}
            {effectiveTab === 'submitted' && (
              <MobileTaskSection
                icon={<CheckCircle2 className="h-5 w-5 text-primary" />}
                title="Submitted"
                tasks={vm.buckets.submitted}
                empty="Nothing submitted yet for this campaign."
              />
            )}
            {effectiveTab === 'approved' && (
              <MobileTaskSection
                icon={<CheckCircle2 className="h-5 w-5 text-primary" />}
                title="Approved"
                tasks={vm.buckets.approvedInCampaign}
                empty="No approved tasks in this campaign yet."
              />
            )}
          </>
        )}
      </main>

      <MobileClaimBottomSheet
        open={!vm.selectedCampaign?.paused && claimSheet === 'task'}
        title="Claim Task"
        onClose={() => setClaimSheet(null)}
      >
        <MobileClaimPanel
          icon={<PlayCircle className="h-5 w-5" />}
          heading="Claim Task"
          unitSingular="task"
          unitPlural="tasks"
          baseLabel="Claim next task"
          options={taskClaimOptions}
          claiming={claiming}
          notice={claimNotice}
          onClaim={onClaimNext}
        />
      </MobileClaimBottomSheet>

      <MobileClaimBottomSheet
        open={!vm.selectedCampaign?.paused && claimSheet === 'review'}
        title="Claim Review"
        onClose={() => setClaimSheet(null)}
      >
        <MobileClaimPanel
          icon={<Eye className="h-5 w-5" />}
          heading="Claim Review"
          unitSingular="review"
          unitPlural="reviews"
          baseLabel="Claim next review"
          singleLabelSuffix="Review"
          options={reviewClaimOptions}
          claiming={claiming}
          notice={claimNotice}
          onClaim={onClaimNextReview}
        />
        <ReviewReleaseBanner campaign={vm.selectedCampaign} />
      </MobileClaimBottomSheet>
    </div>
  );
}



function MobileCampaignSelect({
  campaigns,
  selectedId,
  onChange,
}: {
  campaigns: TaskQueueViewProps['vm']['campaigns'];
  selectedId: string | null;
  onChange: (id: string) => void;
}) {
  if (campaigns.length === 0) {
    return (
      <div className="mb-4 rounded-md border border-gray-200 bg-white px-3 py-3 text-sm text-gray-500">
        No campaigns
      </div>
    );
  }

  return (
    <div className="mb-4">
      <SearchableDropdown
        options={campaigns.map((campaign) => ({
          value: campaign.id,
          label: `${campaign.name}${campaign.archivedAt ? ' (archived)' : ''}`,
          searchText: `${campaign.name} ${campaign.id}`,
        }))}
        value={selectedId}
        onChange={(value) => {
          if (value !== null) onChange(value);
        }}
        label="Campaign"
        placeholder="Select campaign"
        searchPlaceholder="Search campaigns"
        preserveOrder
        triggerClassName="min-h-[46px] text-base"
      />
    </div>
  );
}

function MobileQueueState({ vm }: { vm: TaskQueueViewProps['vm'] }) {
  if (vm.busy) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  if (vm.error) {
    return (
      <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
        {vm.error}
      </div>
    );
  }

  if (vm.accessNotice) {
    return (
      <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
        {vm.accessNotice}
      </div>
    );
  }

  return null;
}

function MobileTabs({
  tabs,
  activeId,
  onChange,
}: {
  tabs: TaskQueueViewProps['tabs'];
  activeId: TabId;
  onChange: (tab: TabId) => void;
}) {
  // A four-across grid overflows below ~360px once the count badges are in,
  // so the tabs scroll horizontally instead of compressing. Each tab keeps a
  // 44px touch target and its label on one line.
  return (
    <div
      className="mb-4 flex gap-1 overflow-x-auto rounded-md bg-gray-200/70 p-1"
      role="tablist"
    >
      {tabs.map((tab) => {
        const active = tab.id === activeId;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.id as TabId)}
            className={`flex min-h-[44px] shrink-0 grow basis-0 items-center justify-center gap-2 whitespace-nowrap rounded px-3 text-sm font-medium transition-colors ${
              active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
            }`}
          >
            <span>{tab.label}</span>
            {tab.count != null && (
              <span className={`rounded-full px-2 py-0.5 text-xs ${active ? 'bg-primary/10 text-primary' : 'bg-white/70 text-gray-600'}`}>
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

interface MobileClaimPanelProps {
  icon: React.ReactNode;
  heading: string;
  unitSingular: string;
  unitPlural: string;
  baseLabel: string;
  singleLabelSuffix?: string;
  options: ClaimOption[];
  claiming: boolean;
  notice: string | null;
  onClaim: (option: ClaimOption | null) => void;
}

function MobileClaimPanel({
  icon,
  heading,
  unitSingular,
  unitPlural,
  baseLabel,
  singleLabelSuffix,
  options,
  claiming,
  notice,
  onClaim,
}: MobileClaimPanelProps) {
  const totalAvailable = options.reduce((total, option) => total + option.availableCount, 0);
  const totalClaimableCount = totalClaimable(options);
  const unit = totalAvailable === 1 ? unitSingular : unitPlural;
  const isMulti = options.length > 1;
  const single = !isMulti ? options[0] : null;

  return (
    <section className="rounded-md border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-primary">
          {icon}
          <div>
            <h2 className="text-base font-semibold text-gray-900">{heading}</h2>
            <p className="text-xs text-gray-500">
              <span className="font-semibold text-gray-700">{totalAvailable}</span> {unit} available
              {single?.shortName && (
                <>
                  {' · '}
                  <span className="text-gray-600">Queue: <span className="font-medium text-gray-800">{single.label}</span></span>
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {isMulti ? (
        <div className="space-y-2">
          {options.map((option) => (
            <button
              key={`${option.taskTypeId ?? '__any__'}::${option.domainId ?? ''}`}
              type="button"
              onClick={() => onClaim(option)}
              disabled={claiming || option.claimingPaused || option.maxReached || option.availableCount === 0}
              className="flex min-h-[46px] w-full items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-800 hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
            >
              <span className="truncate">{claimOptionButtonText(option, baseLabel, singleLabelSuffix)}</span>
              <span className="flex shrink-0 items-center gap-2 text-xs text-gray-500">
                {option.claimingPaused && (
                  <ClaimStatePill label="Paused" />
                )}
                {option.maxReached && (
                  <ClaimStatePill label="Max reached" />
                )}
                <span>{option.availableCount}</span>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onClaim(single)}
          disabled={claiming || totalClaimableCount === 0}
          className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-3 text-sm font-medium text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {claiming && <Loader2 className="h-4 w-4 animate-spin" />}
          <span>
            {claiming
              ? 'Claiming...'
              : single?.claimingPaused
                ? 'Claiming Paused'
                : claimSingleButtonText(baseLabel)}
          </span>
          {single?.maxReached && !claiming && <ClaimStatePill label="Max reached" />}
        </button>
      )}
      {notice && <p className="mt-3 text-xs text-gray-500">{notice}</p>}
    </section>
  );
}


function HeaderClaimButton({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="min-h-[36px] rounded-md bg-primary px-4 text-sm font-medium text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
    >
      Claim
    </button>
  );
}

function MobileClaimBottomSheet({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={title}>
      <button
        type="button"
        aria-label="Close claim sheet"
        onClick={onClose}
        className="absolute inset-0 h-full w-full bg-gray-900/40"
      />
      <div className="absolute inset-x-0 bottom-0 flex max-h-[85vh] flex-col rounded-t-md bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {/* A campaign with many task types makes this list taller than a
            phone viewport — scroll the body, not the page behind it. */}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

// Single-type case: always return the generic baseLabel ("Claim next
// task" / "Claim next review"). Type disambiguation lives in the
// "Type:" line on the card, not the button. Used by the single-type
// MobileClaimPanel button.
function claimSingleButtonText(baseLabel: string): string {
  return baseLabel;
}

// Multi-type case: each per-option button needs its type name so the
// expert can pick between them. Used by the multi-type MobileClaimPanel
// rows that render one button per claimable type.
function claimOptionButtonText(
  option: ClaimOption,
  baseLabel: string,
  singleLabelSuffix?: string,
): string {
  if (!option.shortName) return baseLabel;
  return `Claim next ${option.label}${singleLabelSuffix ? ` ${singleLabelSuffix}` : ''}`;
}

function MobileTaskSection({
  icon,
  title,
  tasks,
  empty,
  action,
  notice,
  statusCounts,
  statusVariant = 'detailed',
  showEscalation = false,
}: {
  icon: React.ReactNode;
  title: string;
  tasks: Task[];
  empty: string;
  action?: React.ReactNode;
  notice?: React.ReactNode;
  /** Campaign-wide task breakdown shown under the section header. */
  statusCounts?: CampaignStatusCounts | null;
  statusVariant?: CampaignProgressBarVariant;
  showEscalation?: boolean;
}) {
  return (
    <section>
      {/* Wraps so the action cluster (sort + browse + claim) drops to its own
          row instead of squeezing the title on a narrow phone. */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-0 items-center gap-2">
          {icon}
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <span className="text-sm text-gray-400">({tasks.length})</span>
        </div>
        {action}
      </div>
      {statusCounts !== undefined && (
        <div className="mb-3">
          <CampaignProgressBar
            counts={statusCounts}
            loading={statusCounts === null}
            variant={statusVariant}
            legend="nonzero"
          />
        </div>
      )}
      {notice}
      {tasks.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-500">
          {empty}
        </div>
      ) : (
        <MobileTaskTable tasks={tasks} showEscalation={showEscalation} />
      )}
    </section>
  );
}

/**
 * Phone-sized task list. Deliberately just the title and one state column —
 * writer, description, dates, ids, and comment counts all live on the task
 * page, and on a 320px screen they turned every row into a paragraph. Rows
 * are full-width tap targets to the task.
 */
function MobileTaskTable({ tasks, showEscalation }: { tasks: Task[]; showEscalation: boolean }) {
  return (
    <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
            <th scope="col" className="px-3 py-2 text-left font-medium">Task</th>
            <th scope="col" className="w-28 px-3 py-2 text-right font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => {
            const state = taskStateDisplay(task, showEscalation);
            return (
              <tr key={task.id} className="border-b border-gray-100 last:border-b-0 active:bg-gray-50">
                <td className="px-3">
                  <Link
                    to={paths.task(task.id)}
                    className="flex min-h-[48px] items-center text-gray-900"
                  >
                    <span className="truncate font-medium">{task.title || 'Untitled task'}</span>
                  </Link>
                </td>
                <td className="px-3">
                  {/* Duplicate link so the whole row is tappable; hidden from
                      assistive tech, which already has the title link. */}
                  <Link
                    to={paths.task(task.id)}
                    aria-hidden="true"
                    tabIndex={-1}
                    className="flex min-h-[48px] items-center justify-end"
                  >
                    <span className={`truncate text-xs ${state.tone}`}>{state.label}</span>
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One-word-ish state for the table's second column. Escalation outranks the
 * status because it is the only thing in the row a reviewer must act on.
 */
// Mobile renders the state as coloured text; desktop uses background pills.
// The LABEL comes from the shared source either way.
const MOBILE_STATE_TONES: Record<QueueTaskState, string> = {
  escalated: 'font-medium text-amber-700',
  claimed: 'text-gray-500',
  in_progress: 'text-gray-500',
  waiting: 'text-blue-700',
  under_review: 'text-amber-700',
  rework: 'text-rose-700',
  approved: 'text-emerald-700',
  archived: 'text-gray-500',
  available: 'text-gray-500',
};

function taskStateDisplay(task: Task, showEscalation: boolean): { label: string; tone: string } {
  const state = queueTaskState(task, { showEscalation });
  return { label: queueTaskStateLabel(task, { showEscalation }), tone: MOBILE_STATE_TONES[state] };
}
